"use client";

import { useCallback, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/modern/utils";
import { useOptionalFileDetailModal } from "../../FileDetailModal/context/FileDetailModalContext";
import { useAnalyzeFileContext } from "../context/AnalyzeFileContext";
import type { AnalyzePresentation } from "../analyzeConstants";
import { AnalyzeInsightsFeed } from "./AnalyzeInsightsFeed";
import { AnalyzeRunBar } from "./AnalyzeRunBar";
import { AnalyzeUnifiedHeader } from "./AnalyzeUnifiedHeader";

export type { AnalyzePresentation };

export interface AnalyzeWorkspaceProps {
  presentation: AnalyzePresentation;
  isLocked: boolean;
  onDeleteMessage: (id: string) => void;
  isLoadingOverlay?: boolean;
  onClose?: () => void;
  title?: string;
  subtitle?: string;
  isDetached?: boolean;
  onToggleDetach?: () => void;
}

export const AnalyzeWorkspace = ({
  presentation,
  isLocked,
  onDeleteMessage,
  isLoadingOverlay,
  onClose,
  title = "Analyze",
  subtitle,
  isDetached,
  onToggleDetach,
}: AnalyzeWorkspaceProps) => {
  const {
    messages,
    personas,
    handlePersonaSelect,
    typingPersonas,
    feedFilter,
    setFeedFilter,
    generationSuccessNotice,
    acknowledgeGenerationSuccess,
  } = useAnalyzeFileContext();
  const fileDetailModal = useOptionalFileDetailModal();
  const onOpenSharePermissions =
    presentation === "panel" ? fileDetailModal?.handleNavigateToSharePermissions : undefined;

  const showShareNudge =
    !!onOpenSharePermissions &&
    !!generationSuccessNotice &&
    !!feedFilter &&
    generationSuccessNotice.personaId === feedFilter;

  const handleShareNudgeClick = useCallback(() => {
    onOpenSharePermissions?.();
    acknowledgeGenerationSuccess();
  }, [onOpenSharePermissions, acknowledgeGenerationSuccess]);

  const chipSize: "md" | "lg" = isDetached ? "md" : presentation === "panel" ? "md" : "lg";

  const insightCountByPersona = useMemo(() => {
    const counts = new Map<string, number>();
    messages.forEach((m) => {
      counts.set(m.personaId, (counts.get(m.personaId) ?? 0) + 1);
    });
    return counts;
  }, [messages]);

  const totalInsights = messages.length;

  const focusedPersona = useMemo(
    () => (feedFilter ? personas.find((p) => p.id === feedFilter) ?? null : null),
    [feedFilter, personas],
  );

  const isGeneratingFocused = feedFilter ? typingPersonas.has(feedFilter) : false;
  const hasInsightsForPersona = feedFilter ? (insightCountByPersona.get(feedFilter) ?? 0) > 0 : false;

  const handleRunAnalysis = useCallback(() => {
    if (!focusedPersona || isLocked || isGeneratingFocused) return;
    handlePersonaSelect(focusedPersona);
  }, [focusedPersona, isLocked, isGeneratingFocused, handlePersonaSelect]);

  return (
    <div
      className={cn(
        "relative flex flex-1 flex-col min-h-0 overflow-hidden",
        isDetached ? "bg-card" : "bg-background/30",
      )}
    >
      {isLoadingOverlay && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading advisors...</p>
          </div>
        </div>
      )}

      <AnalyzeUnifiedHeader
        personas={personas}
        insightCountByPersona={insightCountByPersona}
        feedFilter={feedFilter}
        onFilterChange={setFeedFilter}
        isLocked={isLocked}
        chipSize={chipSize}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        totalInsights={totalInsights}
        isDetached={isDetached}
        onToggleDetach={onToggleDetach}
        onOpenSharePermissions={onOpenSharePermissions}
      />

      <AnalyzeInsightsFeed messages={messages} feedFilter={feedFilter} onDeleteMessage={onDeleteMessage} />

      <AnalyzeRunBar
        focusedPersona={focusedPersona}
        isGenerating={isGeneratingFocused}
        isLocked={isLocked}
        hasInsightsForPersona={hasInsightsForPersona}
        onRun={handleRunAnalysis}
        showShareNudge={showShareNudge}
        onShareNudgeClick={handleShareNudgeClick}
      />
    </div>
  );
};
